import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';

export type SupportedLanguage = 'en' | 'pl';

// Language context for internationalization

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = 'omniflyer_language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'pl'];

/**
 * Detect browser language and return supported language code
 */
function detectBrowserLanguage(): SupportedLanguage {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const browserLang = navigator.language || (navigator as any).userLanguage;
  const langCode = browserLang.split('-')[0].toLowerCase();

  // Check if browser language is supported
  if (SUPPORTED_LANGUAGES.includes(langCode as SupportedLanguage)) {
    return langCode as SupportedLanguage;
  }

  // Check for Polish variants (pl-PL, pl, etc.)
  if (langCode === 'pl' || browserLang.toLowerCase().startsWith('pl')) {
    return 'pl';
  }

  return DEFAULT_LANGUAGE;
}

/**
 * Get language from localStorage or detect from browser
 */
function getInitialLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }

  const detected = detectBrowserLanguage();
  // Store detected language for future use
  localStorage.setItem(LANGUAGE_STORAGE_KEY, detected);
  return detected;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);

  // Load translations dynamically
  const [translations, setTranslations] = useState<any>({});

  useEffect(() => {
    // Dynamically import translation file
    import(`../locales/${language}.json`)
      .then((module) => {
        setTranslations(module.default);
      })
      .catch((error) => {
        logger.error('Failed to load translations:', error);
        // Fallback to English if translation file fails to load
        if (language !== 'en') {
          import('../locales/en.json')
            .then((module) => setTranslations(module.default))
            .catch(() => {
              // If even English fails, use empty object
              setTranslations({});
            });
        }
      });
  }, [language]);

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    // Handle nested keys like "navigation.dashboard"
    const keys = key.split('.');
    let translation: any = translations;
    
    // Navigate through nested object
    for (const k of keys) {
      if (translation && typeof translation === 'object' && k in translation) {
        translation = translation[k];
      } else {
        // Key not found, return the key itself
        translation = key;
        break;
      }
    }
    
    // If translation is still an object, it means we didn't find the final key
    if (typeof translation !== 'string') {
      translation = key;
    }

    // Replace parameters in translation string
    if (params && typeof translation === 'string') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(
          new RegExp(`{{${paramKey}}}`, 'g'),
          String(paramValue)
        );
      });
    }

    return String(translation);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Export t function for use outside of React components
export function useTranslation() {
  return useLanguage();
}
